import { useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff } from "lucide-react";

/**
 * Self-service password change. Requires the user's current password so that
 * a stolen session token alone cannot repurpose an account. Runners who just
 * logged in with an admin-issued temp password should be nudged here.
 */
export default function ChangePasswordDialog({ children }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);

  const reset = () => {
    setCur("");
    setNext("");
    setConfirm("");
    setShow(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!cur || !next) {
      toast.error("Fill in both current and new password");
      return;
    }
    if (next.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (next !== confirm) {
      toast.error("New password and confirmation do not match");
      return;
    }
    if (next === cur) {
      toast.error("New password must be different from the current one");
      return;
    }
    try {
      setBusy(true);
      await api.post("/auth/change-password", {
        current_password: cur,
        new_password: next,
      });
      toast.success("Password updated. Keep it safe.");
      reset();
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Password change failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        data-testid="change-password-dialog"
        className="bg-zinc-950 border-zinc-800 rounded-none text-white max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-wider flex items-center gap-2">
            <KeyRound size={18} className="text-orange-500" />
            CHANGE PASSWORD
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <p className="text-xs text-zinc-400 leading-relaxed">
            If your admin sent you a temporary password like{" "}
            <span className="font-mono text-orange-500">OSCR-ABC-123</span>, this is where you
            replace it with your own.
          </p>

          <PwField
            label="CURRENT PASSWORD"
            value={cur}
            onChange={setCur}
            show={show}
            dt="cp-current"
          />
          <PwField
            label="NEW PASSWORD (MIN 6 CHARS)"
            value={next}
            onChange={setNext}
            show={show}
            dt="cp-new"
          />
          <PwField
            label="CONFIRM NEW PASSWORD"
            value={confirm}
            onChange={setConfirm}
            show={show}
            dt="cp-confirm"
          />

          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="font-mono text-[10px] tracking-widest uppercase text-zinc-500 hover:text-orange-500 inline-flex items-center gap-1"
          >
            {show ? <EyeOff size={12} /> : <Eye size={12} />}
            {show ? "Hide passwords" : "Show passwords"}
          </button>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              onClick={() => setOpen(false)}
              variant="outline"
              className="rounded-none border-zinc-700 bg-transparent text-white hover:bg-zinc-900 font-display tracking-wider"
            >
              CANCEL
            </Button>
            <Button
              data-testid="cp-submit"
              type="submit"
              disabled={busy}
              className="rounded-none bg-orange-600 hover:bg-orange-500 font-display tracking-wider"
            >
              {busy ? "SAVING…" : "UPDATE PASSWORD"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PwField({ label, value, onChange, show, dt }) {
  return (
    <div>
      <label className="font-display tracking-widest text-xs text-zinc-400">{label}</label>
      <Input
        data-testid={dt}
        type={show ? "text" : "password"}
        autoComplete="off"
        spellCheck={false}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 rounded-none bg-zinc-950 border-zinc-800 text-white font-mono"
      />
    </div>
  );
}
